import { useState } from "react";
import { Lock } from "lucide-react";

// Client code = 2 letters + MMYY (month 01-12), e.g. LB0615
export const normalizeClientCode = (raw) =>
  (raw || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 6);

export const isValidClientCode = (code) => /^[A-Z]{2}(0[1-9]|1[0-2])\d{2}$/.test(code || "");

// Read & validate a client code from the current URL (?code=...).
// Returns the normalised valid code, or "" when absent/invalid.
export const readUrlClientCode = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get("code");
    const code = normalizeClientCode(raw);
    return isValidClientCode(code) ? code : "";
  } catch {
    return "";
  }
};

const HELP =
  "Make this from the first letter of their first name and the first letter of their last name (the person being assessed), then their birth month and year. Example: Sam Taylor, born June 2015 \u2192 ST0615.";
const INVALID_MSG =
  "Please check your code: two letters then the month and year of birth, like ST0615.";

export default function ClientCodeField({ code, setCode, noCode, setNoCode, codeRequired, prefilled, onChange }) {
  const [touched, setTouched] = useState(false);
  const showInvalid = touched && code && !isValidClientCode(code);

  if (noCode && !codeRequired) {
    return (
      <div className="sp-field" data-testid="client-code-block">
        <button
          type="button"
          className="cc-link"
          data-testid="client-code-have-code"
          onClick={() => setNoCode(false)}
        >
          I have a code
        </button>
      </div>
    );
  }

  // Pre-filled from the link: lock it (read-only) and protect it.
  if (prefilled && isValidClientCode(code)) {
    return (
      <div className="sp-field" data-testid="client-code-block">
        <label>Client code</label>
        <div className="cc-filled cc-locked" data-testid="client-code-filled">
          <span className="cc-filled-value" data-testid="client-code-filled-value">{code}</span>
          <Lock size={16} className="cc-lock-icon" aria-hidden="true" />
        </div>
        <p className="cc-help" data-testid="client-code-locked-note">This code has been filled in for you. Please leave it as it is.</p>
        <button
          type="button"
          className="cc-link"
          data-testid="client-code-not-this-person"
          onClick={() => { setCode(""); onChange && onChange(); }}
        >
          This isn't the right person
        </button>
      </div>
    );
  }

  return (
    <div className="sp-field" data-testid="client-code-block">
      <label htmlFor="client-code-input">Client code</label>
      <input
        id="client-code-input"
        data-testid="client-code-input"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        placeholder="ST0615"
        value={code}
        onChange={(e) => setCode(normalizeClientCode(e.target.value))}
        onBlur={() => setTouched(true)}
        className="sp-input"
        style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
      />
      <p className="cc-help" data-testid="client-code-help">{HELP}</p>
      {showInvalid && (
        <p className="cc-error" data-testid="client-code-error">{INVALID_MSG}</p>
      )}
      {!codeRequired && (
        <button
          type="button"
          className="cc-link"
          data-testid="client-code-no-code"
          onClick={() => { setNoCode(true); setCode(""); }}
        >
          I don't know their date of birth
        </button>
      )}
    </div>
  );
}
